import React from 'react';
import {
    ExamApplicationStatus,
    ExamStatus,
    ExamType,
    MappedExamTypes,
    MappedExamStatuses,
    MappedExamApplicationStatuses,
    AddressLink,
    renderRow,
} from 'components';
import {
    Color,
    createYandexMapLink,
    createExamDate,
    ColorStatus,
} from '_helpers';
import { Alert } from 'react-bootstrap';
import { IApiError } from '../../_helpers/ApiConnector/types';
import moment from 'moment';
import styles from './ExamApplicationDetails.module.scss';
import addressIcon from 'components/assets/svg/link_icon.svg';

const ExamApplicationDetails = ({
    examApplicationInfo,
    reviewText,
    apiError,
    examStatus,
    examApplicationStatus,
    isApplicant,
    applicantExam,
}: IProps) => {
    if (apiError) {
        return (
            <Alert variant="danger">
                {apiError.message}
            </Alert>
        );
    }

    const changeStatusText = (text: string) => {
        if (text === 'Сдан') return 'Экзамен сдан';
        if (text === 'Не сдан') return 'Экзамен не сдан';

        return text;
    };

    const isResult = () => {
        if (examApplicationStatus && (
            examApplicationStatus === ExamApplicationStatus.NotPassed
            || examApplicationStatus === ExamApplicationStatus.Passed)) {
            return true;
        }

        return false;
    };

    const isExamStartedOrProcessed = () => {
        if (examApplicationStatus && (
            examApplicationStatus === ExamApplicationStatus.IsPassing
            || examApplicationStatus === ExamApplicationStatus.Absent
            || examApplicationStatus === ExamApplicationStatus.NotPassed
            || examApplicationStatus === ExamApplicationStatus.Passed)) {
            return true;
        }

        return false;
    };

    const createColorStatus = () => {
        if (examApplicationStatus) return MappedExamApplicationStatuses[examApplicationStatus];
        if (examStatus) return MappedExamStatuses[examStatus];

        return null;
    };

    const getApplicantStatus = () => {
        if (applicantExam && examApplicationStatus && !applicantExam.processedByNark
            && (examApplicationStatus === ExamApplicationStatus.Passed || examApplicationStatus === ExamApplicationStatus.NotPassed)) {
            return new ColorStatus(Color.Default, 'Обработка');
        }

        const status = examApplicationStatus || examStatus || ExamApplicationStatus.Draft;

        const statuses = {
            ...MappedExamApplicationStatuses,
            [ExamStatus.IsApplying as string]: new ColorStatus(Color.Success, 'Идет запись'),
            [ExamStatus.ApplyingFinished as string]: new ColorStatus(Color.Orange, 'Запись завершена'),
            [ExamApplicationStatus.InReview as string]: new ColorStatus(Color.Default, 'Заявка находится на рассмотрении'),
            [ExamApplicationStatus.CorrectionsRequired as string]: new ColorStatus(Color.Red, 'Требуются корректировки по заявке:'),
            [ExamApplicationStatus.PaymentRequired as string]: new ColorStatus(Color.Success, `Требуется оплата экзамена ${applicantExam && Math.round(applicantExam.examPrice)} Руб.`),
            [ExamApplicationStatus.Paid as string]: new ColorStatus(Color.Default, 'Вы записаны'),
        };

        return statuses[status];
    };

    const createAddress = () => (
        <div>
            <span>{examApplicationInfo.address}</span>
            {!isExamStartedOrProcessed() && (
                <>
                    <img className={styles.address_icon} src={addressIcon} alt="address_icon" />
                    <a
                      href={createYandexMapLink(examApplicationInfo.city, examApplicationInfo.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                        Смотреть на карте
                    </a>
                </>
            )}
        </div>
    );

    const renderColorValue = (cStatus: ColorStatus) => {
        if (examApplicationStatus === ExamApplicationStatus.Passed && applicantExam && applicantExam.processedByNark) {
            return (
                <>
                    {changeStatusText(cStatus.text)}
                    <img className={styles.address_icon} src={addressIcon} alt="address_icon" />
                    <a
                      href={examApplicationInfo.narkPageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                        Запись в реестре НАРК
                    </a>
                </>
            );
        }

        return changeStatusText(cStatus.text);
    };

    const getColorStatus = () => {
        if (isApplicant) return getApplicantStatus();

        return createColorStatus();
    };

    const colorStatus = getColorStatus();

    return (
        <section>
            {renderRow('тип экзамена', MappedExamTypes[examApplicationInfo.type].text, `var(${MappedExamTypes[examApplicationInfo.type].color})`)}

            {renderRow('квалификация', examApplicationInfo.qualificationTitle)}

            {renderRow('цок', examApplicationInfo.qualificationEvaluationCenterName)}

            {renderRow('место проведения', <AddressLink {...examApplicationInfo} withoutLink />)}

            {/* {renderRow('проф. стандарт', examApplicationInfo.occupationalStandardTitle)} */}

            {/* {examApplicationInfo.recertification ? (
                renderRow('тип, экзамена', 'Признание аттестата', `var(${Color.Primary})`)
            ) : null} */}

            {renderRow('дата экзамена', examApplicationInfo.date.format('DD.MM.YYYY'))}

            {renderRow('время проведения', examApplicationInfo.date.format('HH:mm'))}

            {/* {renderRow('город', examApplicationInfo.city)}

            {renderRow('адрес', createAddress())} */}

            {examApplicationInfo.applicationSubmitDate ? (
                renderRow('дата подачи', createExamDate(examApplicationInfo.applicationSubmitDate, examApplicationInfo.applicationSubmitDate))
            ) : null}

            {colorStatus ? (
                renderRow(isResult() ? 'результат' : 'статус', renderColorValue(colorStatus), `var(${colorStatus.color})`)
            ) : null}

            {reviewText ? (
                renderRow('', <i className={styles.reviewText}>«{reviewText}»</i>)
            ) : null}

            {examApplicationInfo.recommendations && examApplicationStatus === ExamApplicationStatus.NotPassed ? (
                renderRow('рекомендация', <span style={{ whiteSpace: 'pre-wrap' }}>{examApplicationInfo.recommendations}</span>)
            ) : null}
        </section>
    );
};

export default ExamApplicationDetails;

interface IExamApplicationInfo {
    date: moment.Moment;
    qualificationTitle: string;
    qualificationLevel: string;
    occupationalStandardTitle: string;
    qualificationEvaluationCenterName?: string;
    address: string;
    city: string;
    type: ExamType;
    applicationSubmitDate?: moment.Moment;
    recertification?: boolean;
    recommendations?: string;
    reviewComment?: string;
    narkPageUrl?: string;
}

interface IApplicantExam {
    examPrice: number;
    processedByNark: boolean;
}

interface IProps {
    examApplicationInfo: IExamApplicationInfo;
    examStatus?: ExamStatus | null;
    examApplicationStatus?: ExamApplicationStatus | null;
    applicantExam?: IApplicantExam | null;
    reviewText?: string | null;
    apiError?: IApiError | null;
    isApplicant?: boolean;
}
